import React, { useState } from "react";
import { normalizeStatus } from "../../shared/useWantList";
import { formatUSD } from "../../shared/utils";
import { CardZoom, BulkFilter, isBulk, loadBulkPref, saveBulkPref } from "../../ui";

// Three. "Want" duplicated Everything, since it's the status every card starts
// on, and "Maybe" is gone entirely — a card you're unsure about just comes off
// the list. PSA 10 stays because it changes what you'd pay, not just how keen
// you are, so it's the one cut worth keeping.
/* Plainly named, and only the cut that changes what you do next. "Still
   hunting" and "In the binder" were the way one person talked about their own
   collection, which reads as jargon to anyone else; and a PSA 10 filter asked
   a question about grading in the middle of a list about ownership. */
const FILTERS = [
  ["all", "All"],
  ["hunting", "Wanted"],
  ["have", "Owned"],
];

export default function WantList({
  wantList,
  removeFromWantList,
  cycleStatus,
  toggleHave,
  onGoToSearch,
  title = "Want to buy",
  emptyMessage,
  onMoveToCollection,      // (card) => fetches fresh prices, adds to collection
  onRemoveFromCollection,  // (cardId) => undoes it
  isInCollection,
  movingId,
  moveError,
  prices,          // { [card code]: number | null } — omitted for lists with no price source
  pricesState,
}) {
  const [filter, setFilter] = useState("all");
  const [zoom, setZoom] = useState(null);
  const [bulk, setBulk] = useState(loadBulkPref);
  const changeBulk = (next) => { setBulk(next); saveBulkPref(next); };

  // Only cards that came from a search carry a real PokemonPriceTracker id, and
  // without one there's nothing to look a price up by. The seeded Lv.X and One
  // Piece lists use synthetic ids, so they don't offer the move at all.
  const canPrice = (card) => /^\d+$/.test(String(card.id));

  /* Owning a card is recorded in one place: the collection. It used to be
     recorded twice — a `have` flag here and membership there — which could
     and did disagree, since marking got by hand changed only one of them.
     Where a card can enter the collection, that membership *is* the answer.

     Two lists can't work that way: a Lv.X master set and a One Piece list
     are checklists of synthetic ids with nothing to price, so they keep
     their own tick. That's a checklist, not a claim about the collection. */
  const ownedByCollection = (card) => !!isInCollection && canPrice(card);
  const isHad = (card) => (ownedByCollection(card) ? isInCollection(card.id) : !!card.have);

  // Only offered where prices exist to judge by. A Pokemon list carries no
  // prices at all, so there'd be nothing to hide on.
  const canFilterBulk = !!prices;
  const bulkCount = canFilterBulk
    ? wantList.filter((c) => isBulk(prices[c.number], bulk.under)).length
    : 0;

  if (!wantList.length) {
    return (
      <div className="section-empty">
        <h2 className="section-title">{title}</h2>
        <p>
          {emptyMessage || (
            <>
              Nothing on the list yet. Search for a card and hit <strong>Want</strong> — it
              lands here so you know what you're after before you walk into a show.
            </>
          )}
        </p>
        {onGoToSearch && <button className="btn" onClick={onGoToSearch}>Go to Search</button>}
      </div>
    );
  }

  const visible = wantList.filter((c) => {
    if (canFilterBulk && bulk.hide && isBulk(prices[c.number], bulk.under)) return false;
    if (filter === "all") return true;
    if (filter === "have") return isHad(c);
    if (filter === "hunting") return !isHad(c);
    return normalizeStatus(c.status) === filter;
  });

  // Derived, not stored — so it can't drift from what the collection says.
  const haveCount = wantList.filter(isHad).length;

  return (
    <>
      <h2 className="section-title">{title}</h2>

      {/* Counts up only: what's in the binder and how long the list is. Never a
          completion percentage — this list doesn't measure what's missing. */}
      <p className="wl-tally">
        {haveCount > 0
          ? <><strong>{haveCount} in the binder</strong> · {wantList.length} cards on the list</>
          : <>{wantList.length} cards on the list · nothing marked yet</>}
        {isInCollection && (
          <span className="wl-tally-note">
            {" "}· got it means it's in your collection
          </span>
        )}
      </p>

      {moveError && <p className="msg-error">{moveError}</p>}

      <div className="wl-filters">
        {FILTERS.map(([key, label]) => (
          <button
            key={key}
            className={filter === key ? "wl-chip wl-chip--active" : "wl-chip"}
            aria-pressed={filter === key}
            onClick={() => setFilter(key)}
          >
            {label}
          </button>
        ))}
        {canFilterBulk && (
          <BulkFilter
            hide={bulk.hide}
            under={bulk.under}
            onChange={changeBulk}
            hiddenCount={bulkCount}
          />
        )}
      </div>

      {visible.length === 0 ? (
        <p className="msg-empty">Nothing here under this filter</p>
      ) : (
        <div className="card-grid">
          {visible.map((card) => {
            const had = isHad(card);
            const derived = ownedByCollection(card);
            return (
            <div key={card.id} className={had ? "card card--have" : "card"}>
              {had && <div className="wl-stamp">Got it</div>}
              <button
                type="button"
                className="card-zoom-btn"
                onClick={() => setZoom(card)}
                title={`Enlarge ${card.name}`}
                aria-label={`Enlarge ${card.name}`}
              >
                <img className="card-image" src={card.imageUrl} alt={card.name} />
              </button>
              <div className="card-name">{card.name}</div>
              {/* Prices key on the full code, but the suffix in OP13-118_p2 is
                  ours and is printed on no card. Stripped for display, which
                  also tidies lists saved before this existed. Pokemon numbers
                  never carry an underscore, so this is a no-op for them. */}
              {card.number && (
                <div className="card-number">
                  {card.displayNumber || String(card.number).split("_")[0]}
                </div>
              )}
              {card.setName && <div className="wl-set">{card.setName}</div>}
              {card.note && <div className="wl-note">{card.note}</div>}

              {prices && (() => {
                const p = prices[card.number];
                if (Number.isFinite(p)) return <div className="card-price">{formatUSD(p)}</div>;
                if (pricesState === "loading") return <div className="wl-price-none">PRICING…</div>;
                // Deliberately blank: the print isn't in the price source, and
                // another print's price would be wrong by a long way.
                return (
                  <div className="wl-price-none" title="This exact print isn't in the price source">
                    NO PRICE FOR THIS PRINT
                  </div>
                );
              })()}

              <div className="card-actions wl-actions">
                {/* No status pill. Want and PSA 10 were two ways to want a
                    card, but a list of cards you are after does not need to
                    know how you would rather own one — and the control sat
                    beside "Owned" looking like it meant something about the
                    card you already have. Grading is its own screen. */}
                {/* There used to be two buttons here that both meant "I own
                    this" — a local tick and Add to Collection — and nothing
                    kept them agreeing. One button, and where a collection
                    exists it is the collection that answers. */}
                {derived ? (
                  <button
                    className="wl-mark"
                    onClick={() =>
                      had ? onRemoveFromCollection?.(card.id) : onMoveToCollection?.(card)
                    }
                    disabled={movingId === card.id}
                    title={
                      had
                        ? "In your collection — click to take it back out"
                        : "Adds it to your collection at today's price"
                    }
                  >
                    {movingId === card.id ? "Adding…" : had ? "In collection" : "Mark got"}
                  </button>
                ) : (
                  <button
                    className="wl-mark"
                    onClick={() => toggleHave(card.id)}
                    title="Ticked here only — this card can't be priced, so it doesn't go in the collection"
                  >
                    {had ? "Got it" : "Mark got"}
                  </button>
                )}
              </div>

              <button className="btn btn-remove" onClick={() => removeFromWantList(card.id)}>
                Remove
              </button>
            </div>
            );
          })}
        </div>
      )}

      <CardZoom card={zoom} onClose={() => setZoom(null)} />
    </>
  );
}
